/**
 * Utilidades para el botón "Confirmar por WhatsApp" del portal terapeuta/director.
 *
 * Usado desde AppointmentModal (TherapistCalendar) y ClinicAppointmentModal
 * (ClinicCalendar) — botón que arma un link wa.me para confirmar asistencia
 * del paciente el día antes de su sesión.
 */

import { toSantiagoDateStr, toSantiagoTime } from './dateUtils';

/**
 * Normaliza un teléfono chileno para armar un link de WhatsApp (wa.me).
 *
 * Limpia todo lo que no sea dígito. Si el resultado no empieza con "56"
 * (código país Chile), lo antepone. Dato real de prod (2026-07-28, 98
 * pacientes con teléfono): 44 ya tienen +56, 52 no tienen código país,
 * formato mixto confirmado (espacios, guiones, "+").
 *
 * Si tras normalizar no quedan exactamente 11 dígitos (56 + 9 dígitos de
 * celular chileno), el dato se considera inválido y se devuelve null — no
 * se arma un link roto.
 */
export function normalizePhoneForWhatsapp(telefono: string | null | undefined): string | null {
  if (!telefono) return null;

  const digits = telefono.replace(/\D/g, '');
  if (!digits) return null;

  const withCountryCode = digits.startsWith('56') ? digits : `56${digits}`;

  return withCountryCode.length === 11 ? withCountryCode : null;
}

/**
 * true si la fecha de la consulta (sin la hora) es exactamente hoy+1 día,
 * en zona horaria America/Santiago.
 *
 * Se compara construyendo "mañana" a partir de un probe a mediodía UTC del
 * día de hoy en Santiago (mismo patrón que getSantiagoOffset en dateUtils.ts)
 * en vez de mutar componentes locales de Date con setDate — eso evitaría el
 * bug de borde que el proyecto ya resolvió en otros puntos del calendario
 * (offset de timezone del navegador vs. America/Santiago).
 */
export function isConsultationTomorrow(fechaHoraConsulta: string | Date): boolean {
  const consultaDate = typeof fechaHoraConsulta === 'string' ? new Date(fechaHoraConsulta) : fechaHoraConsulta;
  const consultaDateStr = toSantiagoDateStr(consultaDate);

  const todaySantiagoStr = toSantiagoDateStr(new Date());
  const todayNoonUTC = new Date(`${todaySantiagoStr}T12:00:00Z`);
  const tomorrowNoonUTC = new Date(todayNoonUTC.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowSantiagoStr = toSantiagoDateStr(tomorrowNoonUTC);

  return consultaDateStr === tomorrowSantiagoStr;
}

function getSantiagoHour(date: Date): number {
  return parseInt(toSantiagoTime(date).split(':')[0], 10);
}

/**
 * Arma el mensaje de confirmación de asistencia. El saludo depende de la
 * hora ACTUAL (momento del click), no de la hora de la consulta:
 *   < 12:00 → "buenos días" | 12:00–18:59 → "buenas tardes" | ≥ 19:00 → "buenas noches"
 * Hora actual evaluada en America/Santiago (mismo motivo que isConsultationTomorrow).
 *
 * Incluye el rango horario completo (inicio–fin) en formato 24h, ej.
 * "17:00-18:30", para que el paciente sepa cuándo termina la sesión.
 */
export function buildWhatsappConfirmationMessage(
  patientFirstName: string,
  fechaHoraConsulta: Date,
  fechaHoraFinConsulta: Date
): string {
  const horaActual = getSantiagoHour(new Date());
  const saludo = horaActual < 12 ? 'buenos días' : horaActual < 19 ? 'buenas tardes' : 'buenas noches';
  const horaInicio = toSantiagoTime(fechaHoraConsulta);
  const horaFin = toSantiagoTime(fechaHoraFinConsulta);

  return `Hola ${saludo} estimado(a) ${patientFirstName}, ¿Confirma su asistencia para su sesión de mañana de ${horaInicio}-${horaFin}?`;
}
